// MeasurementGraph.tsx
import React from 'react';
import {MeasurementGraphProps} from './types';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, ReferenceDot } from 'recharts';
// import {differenceInWeeks } from 'date-fns';

export const MeasurementGraph: React.FC<MeasurementGraphProps> = ({
    config,
    graphType,
    showGraph,
    gender,
    value,
    dataSet,
    age,
    percentileColors,
    measurementState,
    gestationWeeks,
  }) => {
    const axisConfig = graphType === 'WHO' ? config.axisConfig.WHO : config.axisConfig.FENTON;

    if (!gender) return null;
    
    // Custom tooltip to show measurement details
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p>{`${graphType === 'WHO' ? 'חודש' : 'שבוע'}: ${label}`}</p>
          {payload.map((entry: any) => (
            <p key={entry.dataKey} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}${config.unit}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

    const DOT_COLORS = {
      current: '#F77389',     // current
      birth: '#1FB5A3',       // birth
      additional: '#979797',  // additional
    } as const;

    const getDotColor = (type: keyof typeof DOT_COLORS) => DOT_COLORS[type];

  // Helper function to format dot label
  const getDotLabel = (value: number, date?: Date) => {
    if (date) {
      // return `${value}${config.unit} - ${date.toLocaleDateString('he-IL')}`;
      return `${value}${config.unit}`;
    }
    return `${value}${config.unit}`;
  };

  const adjustedAge = gestationWeeks ? parseInt(gestationWeeks) : age;

    return (
      <div style={{
        maxHeight: showGraph ? '450px' : '0',
        opacity: showGraph ? 1 : 0,
        overflow: 'hidden',
        transition: 'all 0.5s ease-in-out',
        marginBottom: showGraph ? '2rem' : '0',
      }}>
        {/* <h2 style={{ textAlign: 'center' }}>גרף {graphType} - {config.label} לפי גיל</h2> */}
        <div style={{ height: '370px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dataSet}
              margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="x" 
                type="number" 
                ticks={axisConfig.TICKS_X}
                domain={axisConfig.DOMAIN_X}
                label={{
                  value: graphType === 'WHO' ? 'חודש' : 'שבוע הריון',
                  position: 'bottom',
                  offset: 0
                }}
              />
              <YAxis 
                type="number" 
                tickMargin={25}
                ticks={axisConfig.TICKS_Y}
                domain={axisConfig.DOMAIN_Y}
                label={{ value: config.unit, angle: -90, position: 'insideLeft' }}
              />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              payload={
                graphType === 'פנטון'
                  ? [
                      { value: '97%', type: 'line', color: percentileColors[97] },
                      { value: '90%', type: 'line', color: percentileColors[90] },
                      { value: '50%', type: 'line', color: percentileColors[50] },
                      { value: '10%', type: 'line', color: percentileColors[10] },
                      { value: '3%', type: 'line', color: percentileColors[3] },
                    ]
                  : [
                      { value: '97.7%', type: 'line', color: percentileColors[97.7] },
                      { value: '90%', type: 'line', color: percentileColors[90] },
                      { value: '50%', type: 'line', color: percentileColors[50] },
                      { value: '10%', type: 'line', color: percentileColors[10] },
                      { value: '2.3%', type: 'line', color: percentileColors[2.3] },
                    ]
              }
            />
              {graphType === 'פנטון' ? (
                <>
                  <Line type="monotone" dataKey="y1" stroke={percentileColors[3]} dot={false} name="3%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y2" stroke={percentileColors[10]} dot={false} name="10%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y3" stroke={percentileColors[50]} dot={false} name="50%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y4" stroke={percentileColors[90]} dot={false} name="90%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y5" stroke={percentileColors[97]} dot={false} name="97%" strokeWidth={1.5} />
                  {/* <ReferenceDot x={age} y={parseFloat(value)} r={5} fill="red" stroke="none" /> */}
                </>
              ) : (
                <>
                  <Line type="monotone" dataKey="y1" stroke={percentileColors[2.3]} dot={false} name="2.3%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y3" stroke={percentileColors[10]} dot={false} name="10%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y5" stroke={percentileColors[50]} dot={false} name="50%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y7" stroke={percentileColors[90]} dot={false} name="90%" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="y9" stroke={percentileColors[97.7]} dot={false} name="97.7%" strokeWidth={1.5} />
                  {/* <ReferenceDot x={age} y={parseFloat(value)} r={5} fill="red" stroke="none" /> */}
                </>
              )}

            {/* Current Measurement */}
            {value && (
              <ReferenceDot
                x={age}
                y={parseFloat(value)}
                r={5}
                fill={getDotColor('current')}
                stroke="none"
                label={{ value: getDotLabel(parseFloat(value)), position: 'top' }}
              />
            )}

            {/* Birth Measurement */}
            {measurementState.birthValue && (
              <ReferenceDot 
                x={graphType === 'WHO' ? 0 : adjustedAge} 
                y={parseFloat(measurementState.birthValue)} 
                r={5} 
                fill={getDotColor('birth')} 
                stroke="none"
                label={{ value: getDotLabel(parseFloat(measurementState.birthValue)), position: 'top' }}
              />
            )}

            {/* Additional Measurements */}
            {measurementState.additionalEntries.map((entry, index) => (
              <ReferenceDot 
                key={index} 
                x={entry.week} 
                y={entry.value} 
                r={5} 
                fill={getDotColor('additional')} 
                stroke="none"
                label={{ value: getDotLabel(entry.value, entry.date), position: 'top' }}
              />
            ))}

            </LineChart>
          </ResponsiveContainer>

        </div>
        {/* Legend for measurement points */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginTop: '12px',
          fontSize: '0.9rem',
        }}
      >
        {[
          { key: 'current', label: 'מדידה נוכחית', show: !!value },
          { key: 'birth', label: 'מדידת לידה', show: !!measurementState.birthValue },
          { key: 'additional', label: 'מדידות נוספות', show: measurementState.additionalEntries.length > 0 },
        ]
          .filter(({ show }) => show)
          .map(({ key, label }) => (
            <div
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: DOT_COLORS[key as keyof typeof DOT_COLORS],
                }}
              />
              <span>{label}</span>
            </div>
          ))}
      </div>
    </div>
  );
};
  